export const SCHEDULE_WEEKDAY_HEADER_LABELS = ['Ming', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'] as const;

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UTC_MIDNIGHT_RE = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.\d{1,3})?Z$/i;

/** Persist a calendar `YYYY-MM-DD` as UTC midnight so `.slice(0, 10)` stays stable. */
export function calendarDateToUtcMidnightIso(date: string): string {
  const trimmed = date.trim();
  if (!CALENDAR_DATE_RE.test(trimmed)) {
    throw new Error(`Format tanggal tidak valid: ${date}`);
  }
  return `${trimmed}T00:00:00.000Z`;
}

/**
 * Read a Strapi datetime used as a calendar date.
 * Canonical writes use UTC midnight; legacy `localMidnight.toISOString()` values
 * are recovered via local calendar parts (avoids WIB −1 day from `.slice(0, 10)`).
 */
export function calendarDateFromStoredDateTime(value?: string | null): string | null {
  const text = String(value ?? '').trim();
  if (!text) return null;
  if (CALENDAR_DATE_RE.test(text)) return text;
  const canonical = text.match(UTC_MIDNIGHT_RE);
  if (canonical) return canonical[1];
  const d = new Date(text);
  if (!Number.isNaN(d.getTime())) return isoDate(d);
  const slice = text.slice(0, 10);
  return CALENDAR_DATE_RE.test(slice) ? slice : null;
}

/** Geser tanggal kalender `YYYY-MM-DD` tanpa lewat UTC (hindari mundur 1 hari di WIB). */
export function shiftIsoDateByDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return isoDate(addDays(d, days));
}

/** Awal minggu (Minggu) selaras dengan kolom Min–Sab. */
export function startOfWeek(dateText: string): string {
  const base = dateText ? new Date(`${dateText}T00:00:00`) : new Date();
  if (Number.isNaN(base.getTime())) return isoDate(new Date());
  return isoDate(addDays(base, -base.getDay()));
}

export function addWeeks(weekStartText: string, weeks: number): string {
  const base = new Date(`${weekStartText}T00:00:00`);
  if (Number.isNaN(base.getTime())) return startOfWeek('');
  return startOfWeek(isoDate(addDays(base, weeks * 7)));
}

export function weekDatesFromStart(weekStartText: string): string[] {
  const start = new Date(`${weekStartText}T00:00:00`);
  if (Number.isNaN(start.getTime())) return [];
  return Array.from({ length: 7 }, (_, idx) => isoDate(addDays(start, idx)));
}

/** Tujuh hari berturut-turut mulai tanggal anchor (inklusif). */
export function weekDatesForwardFrom(anchorDate: string, count = 7): string[] {
  const start = new Date(`${anchorDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return [];
  return Array.from({ length: count }, (_, idx) => isoDate(addDays(start, idx)));
}

/** Daftar tanggal ISO dari start sampai end (inklusif). */
export function isoDatesInclusive(startDate: string, endDate: string): string[] {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const dates: string[] = [];
  let cursor = start;
  while (cursor <= end) {
    dates.push(isoDate(cursor));
    cursor = addDays(cursor, 1);
  }
  return dates;
}

/** Rentang survey yang sudah dipilih admin WO (`customer_agreed_*` / date-only slots). */
export function defaultSurveyRangeFromSchedule(opts: {
  dateOnlySlots?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
}): { startDate: string; finishDate: string } | null {
  const slots = (opts.dateOnlySlots ?? [])
    .map((d) => String(d ?? '').trim().slice(0, 10))
    .filter((d) => CALENDAR_DATE_RE.test(d))
    .sort();
  if (slots.length > 0) {
    return { startDate: slots[0]!, finishDate: slots[slots.length - 1]! };
  }
  const start = String(opts.startDate ?? '').trim().slice(0, 10);
  const end = String(opts.endDate ?? '').trim().slice(0, 10);
  if (!CALENDAR_DATE_RE.test(start)) return null;
  const finish = CALENDAR_DATE_RE.test(end) ? end : start;
  return { startDate: start, finishDate: finish < start ? start : finish };
}

export function scheduleDayHeaderLabel(isoDateStr: string): string {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDateStr;
  const weekday = SCHEDULE_WEEKDAY_HEADER_LABELS[d.getDay()] ?? '';
  return `${d.getDate()}-${weekday}`;
}

export function scheduleWeekRangeDateLabel(isoDateStr: string): string {
  return isoDateStr.replace(/-/g, '/');
}

export function scheduleDayTitle(isoDateStr: string): string {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDateStr;
  return d.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
}

export type MonthAnchor = { year: number; month: number };

export function currentMonthAnchor(): MonthAnchor {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function addMonths(anchor: MonthAnchor, delta: number): MonthAnchor {
  const d = new Date(anchor.year, anchor.month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export function monthDatesFromAnchor(anchor: MonthAnchor): string[] {
  const daysInMonth = new Date(anchor.year, anchor.month + 1, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, idx) => isoDate(new Date(anchor.year, anchor.month, idx + 1)));
}

/** Minggu penuh (Min–Sab) yang membentuk grid kalender bulan — termasuk hari di luar bulan. */
export function monthCalendarWeeks(anchor: MonthAnchor): string[][] {
  const firstIso = isoDate(new Date(anchor.year, anchor.month, 1));
  const lastIso = isoDate(new Date(anchor.year, anchor.month + 1, 0));
  const gridStart = startOfWeek(firstIso);
  const lastWeekStart = startOfWeek(lastIso);
  const gridEnd = addDays(new Date(`${lastWeekStart}T00:00:00`), 6);

  const weeks: string[][] = [];
  let cursor = new Date(`${gridStart}T00:00:00`);
  while (cursor <= gridEnd) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(isoDate(cursor));
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

export function isDateInMonth(isoDateStr: string, anchor: MonthAnchor): boolean {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === anchor.year && d.getMonth() === anchor.month;
}

export function dayOfMonthNumber(isoDateStr: string): number {
  const d = new Date(`${isoDateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? 0 : d.getDate();
}

export function monthRangeLabel(anchor: MonthAnchor): string {
  const d = new Date(anchor.year, anchor.month, 1);
  return d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
}

/** Label kompak untuk header kolom bulanan (mis. `3-Sen`). */
export function scheduleMonthDayHeaderLabel(isoDateStr: string): string {
  const d = new Date(`${isoDateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDateStr;
  const weekday = SCHEDULE_WEEKDAY_HEADER_LABELS[d.getDay()] ?? '';
  return `${d.getDate()}-${weekday}`;
}
